<template>
    <AutoComplete
        ref="autocomplete"
        force-selection
        unstyled
        :model-value="autocompleteModel"
        :multiple
        :disabled
        fluid
        :loading="loading"
        :suggestions="data"
        :data-key="optionValue"
        panel-class="!tw-border !tw-border-gray-200 !tw-rounded-md !tw-bg-white !tw-shadow-lg !tw-p-1"
        overlay-class="!tw-bg-white !tw-z-[10001]"
        :input-class="inputClass"
        :pt="{
            inputMultiple: {
                class: [
                    'tw-m-0 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm tw-text-gray-900',
                    {
                        '!tw-border-0 !tw-bg-transparent !tw-p-0 autocomplete-selection-summary-list': selectionSummary,
                        '!tw-bg-gray-100 !tw-text-gray-400 !tw-border-gray-200 !tw-cursor-not-allowed': disabled,
                    },
                ],
            },
            inputChip: {
                class: 'tw-flex tw-min-w-0 tw-flex-1',
            },
            chipItem: {
                class: 'tw-list-none autocomplete-multiple-chip-item',
            },
            listContainer: {
                class: 'tw-overflow-auto',
                onScroll: handleListScroll,
            },
            list: {
                class: 'tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0',
            },
            option: {
                class: 'tw-cursor-pointer tw-p-0',
            },
        }"
        :class="['tw-relative tw-w-full', { 'tw-cursor-not-allowed': disabled }]"
        :placeholder="inputPlaceholder"
        :option-label
        complete-on-focus
        :show-clear="!loading && !disabled && (!multiple || selectionSummary)"
        @update:model-value="($v) => onUpdateModelValue($v)"
        @click="handleContainerClick"
        @complete="(e: AutoCompleteCompleteEvent) => getData(e)"
    >
        <template v-if="loading" #loader>
            <i
                class="autocomplete-loader mdi mdi-loading mdi-spin tw-pointer-events-none tw-absolute tw-right-2 tw-top-1/2 tw-z-10 tw--translate-y-1/2 tw-text-sm tw-text-gray-400"
            ></i>
        </template>
        <template #clearicon="{ clearCallback }">
            <button
                type="button"
                class="autocomplete-clear tw-absolute tw-right-2 tw-top-1/2 tw-z-10 tw-flex tw-h-5 tw-w-5 tw--translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-text-gray-400 tw-transition-colors hover:tw-bg-gray-100 hover:tw-text-gray-600 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-eventwise/30"
                aria-label="Clear selection"
                @click.stop="clearCallback"
            >
                <i class="mdi mdi-close tw-text-sm"></i>
            </button>
        </template>
        <template #option="{ option }">
            <span
                class="tw-flex tw-w-full tw-items-center tw-rounded-md tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-text-gray-900 tw-transition-colors hover:tw-bg-gray-50"
                :class="{
                    'tw-bg-eventwise/10 !tw-text-eventwise hover:tw-bg-eventwise/10': isOptionSelected(option),
                }"
            >
                <span class="tw-min-w-0 tw-flex-1 tw-truncate">
                    <slot name="option" :option="option">
                        {{ getOptionLabel(option) }}
                    </slot>
                </span>
                <i v-if="isOptionSelected(option)" class="mdi mdi-check tw-shrink-0 tw-text-base tw-text-eventwise"></i>
            </span>
        </template>
        <template #footer="{ suggestions }">
            <button
                v-if="canCreate && suggestions.length > 0"
                type="button"
                class="tw-w-full tw-rounded-md tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-text-eventwise tw-transition-colors hover:tw-bg-eventwise/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-eventwise/30"
                @click.stop="handleCreate"
            >
                Create {{ activeSearchTerm }}
            </button>
        </template>
        <template #empty>
            <div v-if="canCreate" class="tw-px-1 tw-py-1">
                <button
                    type="button"
                    class="tw-w-full tw-rounded-md tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-text-eventwise tw-transition-colors hover:tw-bg-eventwise/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-eventwise/30"
                    @click.stop="handleCreate"
                >
                    Create
                    <span class="tw-font-medium">{{ activeSearchTerm }}</span>
                </button>
            </div>
            <div v-else class="tw-px-3 tw-py-8 tw-text-center">
                <i class="mdi mdi-magnify tw-block tw-text-2xl tw-text-gray-300"></i>
                <p class="tw-m-0 tw-text-sm tw-font-medium tw-text-gray-700">No results found</p>
            </div>
        </template>
        <template #chip="{ value, removeCallback }">
            <div class="tw-px-3">
                {{ getOptionLabel(value) }}
                <span class="mdi mdi-close-circle tw-cursor-pointer" @click.stop="removeCallback(value)"></span>
            </div>
        </template>
    </AutoComplete>
</template>
<script
    setup
    lang="ts"
    generic="TMultiple extends boolean = false, TOption extends Record<string, unknown> = Record<string, unknown>"
>
/**
 * For us to 'properly' use autocomplete, the model value needs
 * to match the options, because the model value needs to match the schema
 * of the options, e.g. you can't pass { id: number, integration_id: 'abc123' }
 * as a model value but expect the options to be { id: 'abc123', name: 'The label' }.
 * So what we do is, make model value a list of strings for example (the 'ids'), then
 * we get the options from the API against those ids, so the autocomplete can natively map them
 * to the global options so they're the same format, and just manually update the model value
 *
 * @example
 * ```vue
 * <Autocomplete
 *     v-model="userId"
 *    option-value="id"
 *    option-label="name"
 *    :endpoint="route('api.users')" // returns a list like [ { id: string, name: string } ]
 * />
 * <Autocomplete
 *    v-model="userIds"
 *   option-value="id"
 *   option-label="name"
 *   :endpoint="route('api.users')"
 *   multiple
 * />
 * ```
 */
import { axios } from '@/js/utils/axios.ts';
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import { computed, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue';
import type { PropType } from 'vue';

type ModelValue = TMultiple extends true ? (string | number)[] : string | number | null;
type PaginatedResponse<T> = {
    data: T[];
    next_page_url: string | null;
};

type SelectionValue = TMultiple extends true ? TOption[] : TOption | null;

const model = defineModel<ModelValue>();
const emit = defineEmits<{
    'selection-change': [selection: SelectionValue];
    create: [text: string];
}>();
const props = defineProps({
    endpoint: {
        type: String,
        required: true,
    },
    placeholder: {
        type: String,
        default: null,
    },
    optionLabel: {
        type: [String, Function] as PropType<string | ((option: TOption) => string)>,
        default: 'name',
    },
    optionValue: {
        type: String,
        default: 'id',
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    selectionSummary: {
        type: Boolean,
        default: false,
    },
    allowCreate: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});
type AutoCompleteInstance = InstanceType<typeof AutoComplete> & {
    hide: () => void;
};
const autocomplete = useTemplateRef<AutoCompleteInstance>('autocomplete');
const loading = ref(false);
const data = ref<TOption[]>([]);
const selectedOptions = shallowRef<TOption[]>([]);
const nextPageUrl = ref<string | null>(null);
const activeSearchTerm = ref<string | undefined>(undefined);
const canCreate = computed(() => props.allowCreate && Boolean(activeSearchTerm.value?.trim()));
const localSelectedIds = ref<(string | number)[] | null>(null);
let activeRequestId = 0;
const propSelectedIds = computed<(string | number)[]>(() => {
    const modelValue = model.value as string | number | (string | number)[] | null | undefined;

    if (Array.isArray(modelValue)) {
        return props.multiple ? (modelValue as (string | number)[]) : [];
    }

    if (modelValue === null || modelValue === undefined) {
        return [];
    }

    return [modelValue];
});
const selectedIds = computed<(string | number)[]>(() => localSelectedIds.value ?? propSelectedIds.value);
const inputClass = computed(() => [
    'tw-text-sm tw-border tw-border-gray-300 tw-bg-white tw-w-full tw-px-3.5 tw-py-1.5 !tw-pr-10 tw-rounded-md tw-text-gray-900 tw-transition-all tw-duration-150 placeholder:tw-text-gray-400 hover:tw-border-gray-400 focus:tw-border-gray-600 focus:tw-outline-none',
    {
        'placeholder:!tw-text-slate-800': props.selectionSummary && selectedIds.value.length > 0,
        '!tw-bg-gray-100 !tw-text-gray-400 !tw-border-gray-200 hover:!tw-border-gray-200 !tw-cursor-not-allowed':
            props.disabled,
    },
]);
const getOptionLabel = (option: TOption): string =>
    typeof props.optionLabel === 'function' ? props.optionLabel(option) : String(option[props.optionLabel] ?? '');
const getOptionValue = (option: TOption): string | number => option[props.optionValue] as string | number;
const areIdsEqual = (first: (string | number)[], second: (string | number)[]): boolean =>
    first.length === second.length && first.every((id, index) => String(id) === String(second[index]));
const inputPlaceholder = computed(() => {
    if (!props.selectionSummary || !props.multiple || selectedIds.value.length === 0) {
        return props.placeholder;
    }

    if (selectedIds.value.length > 1) {
        return `(${selectedIds.value.length} selected)`;
    }

    const selectedOption = selectedOptions.value.find(
        (option) => String(getOptionValue(option)) === String(selectedIds.value[0])
    );

    return selectedOption ? getOptionLabel(selectedOption) : props.placeholder;
});
const writeAutocompleteValue = (options: TOption[]): void => {
    if (!autocomplete.value) {
        return;
    }

    if (props.multiple) {
        // @ts-expect-error - PrimeVue exposes writeValue at runtime but omits it from the component instance type.
        autocomplete.value.writeValue(options);

        return;
    }

    // @ts-expect-error - PrimeVue exposes writeValue at runtime but omits it from the component instance type.
    autocomplete.value.writeValue(options[0] ?? null);
};
const isOptionSelected = (option: TOption): boolean => {
    const value = getOptionValue(option);

    return props.multiple
        ? selectedIds.value.some((selectedId) => String(selectedId) === String(value))
        : model.value === value;
};
const getModelValues = (): (string | number)[] => {
    if (props.multiple) {
        return Array.isArray(model.value) ? (model.value as (string | number)[]) : [];
    }

    return model.value === null || model.value === undefined ? [] : [model.value as string | number];
};
const getSelectedOptions = (): TOption[] => {
    return getModelValues().flatMap((value) => {
        const option =
            selectedOptions.value.find((selectedOption) => selectedOption[props.optionValue] === value) ??
            data.value.find((dataOption) => dataOption[props.optionValue] === value);

        return option ? [option as TOption] : [];
    });
};
const autocompleteModel = computed<TOption | TOption[] | null>(() => {
    const selected = getSelectedOptions();

    return props.multiple ? selected : (selected[0] ?? null);
});

const isPaginatedResponse = (responseData: unknown): responseData is PaginatedResponse<TOption> => {
    return (
        typeof responseData === 'object' &&
        responseData !== null &&
        'data' in responseData &&
        Array.isArray(responseData.data)
    );
};

const getResponseOptions = (responseData: unknown): TOption[] => {
    if (Array.isArray(responseData)) {
        return responseData;
    }

    return isPaginatedResponse(responseData) ? responseData.data : [];
};

const updateOptions = (responseData: unknown, append = false): void => {
    const options = getResponseOptions(responseData);

    data.value = (append ? [...data.value, ...options] : options) as TOption[];
    nextPageUrl.value = isPaginatedResponse(responseData) ? (responseData.next_page_url ?? null) : null;
};

const loadNextPage = async (): Promise<void> => {
    const pageUrl = nextPageUrl.value;

    if (props.disabled || loading.value || !pageUrl) {
        return;
    }

    const requestId = activeRequestId;

    loading.value = true;

    try {
        const { data: responseData } = await axios.get(pageUrl, {
            params: {
                search: activeSearchTerm.value,
            },
        });

        if (requestId !== activeRequestId) {
            return;
        }

        updateOptions(responseData, true);
    } finally {
        if (requestId === activeRequestId) {
            loading.value = false;
        }
    }
};

const handleListScroll = (event: Event): void => {
    const target = event.currentTarget;

    if (!(target instanceof HTMLElement)) {
        return;
    }

    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom > 32) {
        return;
    }

    void loadNextPage();
};

onMounted(async () => {
    if (props.disabled) {
        return;
    }

    const requestId = ++activeRequestId;

    loading.value = true;
    const hasModelValue = props.multiple
        ? Array.isArray(model.value) && model.value.length > 0
        : model.value !== null && model.value !== undefined;

    try {
        if (hasModelValue) {
            const { data: responseData } = await axios.get(props.endpoint, {
                params: {
                    selected: Array.isArray(model.value) ? model.value : [model.value],
                },
            });
            if (requestId !== activeRequestId) {
                return;
            }

            updateOptions(responseData);
            const hydratedOptions = getResponseOptions(responseData);
            selectedOptions.value = hydratedOptions;
            writeAutocompleteValue(hydratedOptions);
        }
    } finally {
        if (requestId === activeRequestId) {
            loading.value = false;
        }
    }
});

const getData = async (evt?: AutoCompleteCompleteEvent) => {
    if (props.disabled) {
        return;
    }

    const requestId = ++activeRequestId;

    activeSearchTerm.value = evt?.query;
    nextPageUrl.value = null;
    loading.value = true;

    try {
        const { data: responseData } = await axios.get(props.endpoint, {
            params: {
                search: activeSearchTerm.value,
            },
        });

        if (requestId !== activeRequestId) {
            return;
        }

        updateOptions(responseData);
    } finally {
        if (requestId === activeRequestId) {
            loading.value = false;
        }
    }
};

const onUpdateModelValue = (value: TOption | TOption[] | null) => {
    if (props.multiple && Array.isArray(value)) {
        selectedOptions.value = value as TOption[];
        const selectedOptionIds = value.map(getOptionValue);
        localSelectedIds.value = selectedOptionIds;
        model.value = selectedOptionIds as ModelValue;
        emit('selection-change', value as unknown as SelectionValue);
        return;
    }

    if (!value) {
        selectedOptions.value = [];
        if (props.multiple) {
            localSelectedIds.value = [];
            model.value = [] as unknown as ModelValue;
            emit('selection-change', [] as unknown as SelectionValue);
        } else {
            localSelectedIds.value = null;
            model.value = null as ModelValue;
            emit('selection-change', null as unknown as SelectionValue);
        }
        return;
    }

    const option = value as TOption;
    selectedOptions.value = [option];
    localSelectedIds.value = null;
    model.value = getOptionValue(option) as ModelValue;
    emit('selection-change', option as unknown as SelectionValue);
};

const handleCreate = (): void => {
    const text = activeSearchTerm.value;

    if (!canCreate.value || text === undefined) {
        return;
    }

    emit('create', text);
};

const selectOption = (option: TOption): void => {
    onUpdateModelValue(option);
    writeAutocompleteValue([option]);
};

const open = async (): Promise<void> => {
    if (props.disabled) {
        return;
    }

    await getData();

    if (autocomplete.value && 'show' in autocomplete.value && typeof autocomplete.value.show === 'function') {
        autocomplete.value.show();
    }
};

const close = (): void => {
    autocomplete.value?.hide();
};

defineExpose({ open, close, selectedOptions, selectOption });

/**
 * So annoyingly, when you select the input, the dropdown shows, you click an option,
 * and then the dropdown closes, but the input remains focused, so if you click the input again,
 * it doesn't open the dropdown because it's already focused, so we have to manually
 * open it on click, but only if it's not already open, and only if it's multiple
 * because if it's single, you want it to close when you select an option and click again to open it
 */
const handleContainerClick = () => {
    if (!props.multiple || props.disabled) {
        return;
    }

    // overlayVisible definitely does exist, typescript just isn't being that smart.
    // Quick check to just satisfy tcs
    if (autocomplete.value && !('overlayVisible' in autocomplete.value)) {
        return;
    }
    if (autocomplete.value && !autocomplete.value.overlayVisible) {
        autocomplete.value.overlayVisible = true;
    }
};
watch(
    () => propSelectedIds.value,
    async (modelSelectedIds, previousSelectedIds) => {
        if (!props.selectionSummary || !props.multiple || !autocomplete.value) {
            return;
        }

        if (areIdsEqual(modelSelectedIds, previousSelectedIds)) {
            return;
        }

        if (localSelectedIds.value !== null && areIdsEqual(localSelectedIds.value, modelSelectedIds)) {
            localSelectedIds.value = null;
            return;
        }

        localSelectedIds.value = null;
        const requestId = ++activeRequestId;
        const loadedIds = selectedOptions.value.map(getOptionValue);

        if (areIdsEqual(loadedIds, modelSelectedIds)) {
            if (modelSelectedIds.length === 0) {
                writeAutocompleteValue([]);
                loading.value = false;
            }

            return;
        }

        selectedOptions.value = [];

        if (modelSelectedIds.length === 0) {
            writeAutocompleteValue([]);
            loading.value = false;
            return;
        }

        loading.value = true;

        try {
            const { data: responseData } = await axios.get(props.endpoint, {
                params: {
                    selected: modelSelectedIds,
                },
            });

            if (requestId !== activeRequestId) {
                return;
            }

            updateOptions(responseData);
            const hydratedOptions = getResponseOptions(responseData);
            selectedOptions.value = hydratedOptions;
            writeAutocompleteValue(hydratedOptions);
        } finally {
            if (requestId === activeRequestId) {
                loading.value = false;
            }
        }
    },
    { deep: true }
);
</script>

<style scoped>
:deep(.autocomplete-selection-summary-list > .autocomplete-multiple-chip-item) {
    display: none;
}
</style>
